import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Msg, panelGet, panelPost } from '../../kit';
import { Labeled, inputCls, placeholdersIn } from '../emailKit';
import { DesignBuilder } from '../designs/DesignBuilder';
import { RichText, RichTextHandle } from '../designs/RichText';
import { DesignRow, DesignsMeta, bodyToEditorHtml } from '../designs/types';
import { BodyFormat, EditableContent, FORMAT_LABEL, Placeholder, convertContent, forWire, withKeys } from './types';

/**
 * The e-mail editor Campaigns, MEMAC triggers and Saved templates share
 * (extracted from the Campaigns content step, 2026-10-14). Three formats:
 *
 *   text   — a plain body (a blank line starts a paragraph, "• " a list)
 *            and up to 3 buttons: how the built-in e-mails are written;
 *   html   — rich text (bold, italic, links, lists, or the HTML source),
 *            with an optional plain-text part: Campaigns "Write from scratch";
 *   design — the block builder (Email designs), with its own live preview.
 *
 * Placeholder chips insert at the caret of the field last in focus. A switch
 * to another format converts what is written (lossy, so it asks first). The
 * envelope (From, Reply-To) is EnvelopeFields, beside it; what a placeholder
 * becomes for someone with no value is FallbacksPanel (below), which MEMAC
 * triggers and Saved templates show under the editor.
 */

type Field = 'subject' | 'body' | 'text' | `cta_label_${number}` | `cta_url_${number}`;
const MAX_CTAS = 3;

export const ContentEditor: React.FC<{
  value: EditableContent;
  onChange: (patch: Partial<EditableContent>) => void;
  /** The formats on offer, in this order (one = no switch shown). */
  formats: BodyFormat[];
  placeholders: Placeholder[];
  /** Email designs meta (GET email/designs); fetched here when not given. */
  designs?: DesignsMeta;
  /** Design format: offer "Save as a design…" (to Email designs). */
  allowSaveAsDesign?: boolean;
  disabled?: boolean;
  /** The description a design saved from here gets. */
  saveAsDesignNote?: string;
  /** A line under the format switch (e.g. what the plain text is for). */
  hint?: React.ReactNode;
}> = ({ value, onChange, formats, placeholders, designs, allowSaveAsDesign, disabled, saveAsDesignNote, hint }) => {
  const c = value;
  const refs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});
  const bodyRef = useRef<RichTextHandle>(null);
  const focused = useRef<Field>('body');
  // fmt: a conversion note shows only while the content is in that format
  // (a Discard in the parent puts the old format back, and the note goes).
  const [note, setNote] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string; fmt?: BodyFormat } | null>(null);

  // -- designs meta: given, or fetched once the design format is in use --------
  const [ownDesigns, setOwnDesigns] = useState<DesignsMeta | null>(null);
  const [designsErr, setDesignsErr] = useState('');
  const [designsTick, setDesignsTick] = useState(0);
  const dmeta = designs || ownDesigns;
  const needDesigns = !designs && formats.includes('design') && (c.format === 'design' || designsTick > 0);
  useEffect(() => {
    if (!needDesigns) return;
    let gone = false;
    panelGet('email', 'designs').then(({ data, error }) => {
      if (gone) return;
      if (error) setDesignsErr(error); else { setOwnDesigns(data as DesignsMeta); setDesignsErr(''); }
    });
    return () => { gone = true; };
  }, [needDesigns, designsTick]);
  const library: DesignRow[] = useMemo(() => [...(dmeta?.builtins || []), ...(dmeta?.saved || [])], [dmeta]);

  // A design format with nothing in it starts from the first library design.
  useEffect(() => {
    if (c.format === 'design' && !c.design && library.length && !disabled) {
      onChange({ design: withKeys(library[0].doc), design_from: library[0].name });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.format, c.design, library.length]);
  // A rich-text body written before the editor (plain text) opens as its paragraphs.
  useEffect(() => {
    if (c.format === 'html' && c.body.trim() && !c.raw_html && !disabled) {
      const v = bodyToEditorHtml(c.body);
      if (v !== c.body) onChange({ body: v });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.format]);

  // -- placeholders at the caret ----------------------------------------------
  const getField = (f: Field): string => {
    if (f.startsWith('cta_')) {
      const [, part, i] = f.split('_');
      const x = c.ctas[Number(i)];
      return x ? (part === 'label' ? x.label : x.url) : '';
    }
    return String((c as any)[f] ?? '');
  };
  const setField = (f: Field, v: string) => {
    if (f.startsWith('cta_')) {
      const [, part, i] = f.split('_');
      onChange({ ctas: c.ctas.map((x, j) => (j === Number(i) ? { ...x, [part]: v } : x)) });
    } else onChange({ [f]: v } as Partial<EditableContent>);
  };
  const insert = (name: string) => {
    const token = `{${name}}`;
    const f = focused.current;
    if (f === 'body' && c.format === 'html') { bodyRef.current?.insert(token); return; }
    const el = refs.current[f] || (c.format === 'text' ? refs.current.body : refs.current.subject);
    const field: Field = refs.current[f] ? f : (c.format === 'text' ? 'body' : 'subject');
    const cur = getField(field);
    const start = el?.selectionStart ?? cur.length;
    const end = el?.selectionEnd ?? cur.length;
    setField(field, cur.slice(0, start) + token + cur.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      try { el?.setSelectionRange(start + token.length, start + token.length); } catch { /* not a text field */ }
    });
  };
  const track = (f: Field) => ({
    ref: (el: HTMLInputElement | HTMLTextAreaElement | null) => { refs.current[f] = el; },
    onFocus: () => { focused.current = f; },
  });

  // -- format switch -----------------------------------------------------------
  const empty = c.format === 'design' ? !c.design?.blocks?.length : !c.body.replace(/<[^>]*>/g, '').trim();
  const switchTo = (to: BodyFormat) => {
    if (to === c.format || disabled) return;
    setNote(null);
    if (empty) {
      onChange({ format: to, body: '', text: '', ctas: [], design: null, design_from: '', raw_html: false });
      if (to === 'design') setDesignsTick(t => t + 1);
      return;
    }
    const msg = `Switch to ${FORMAT_LABEL[to]}? What is written is converted, and some of it may be lost `
      + `(${to === 'design' ? 'styling is simplified' : c.format === 'design' ? 'the layout and colours go' : to === 'text' ? 'formatting goes' : 'buttons become links'}).`;
    if (!window.confirm(msg)) return;
    const r = convertContent(c, to, dmeta?.default_theme);
    onChange(r.content);
    if (r.warnings.length) setNote({ kind: 'warn', text: r.warnings.join(' '), fmt: to });
  };

  // -- designs: start from one, save as one ------------------------------------
  const [pick, setPick] = useState('');
  const [saveAs, setSaveAs] = useState<string | null>(null);
  const loadDesign = () => {
    const r = library.find(x => x.id === pick);
    if (!r) return;
    if (c.design && !window.confirm(`Replace this e-mail with “${r.name}”? Your changes here are lost.`)) return;
    onChange({ design: withKeys(r.doc), design_from: r.name });
    setPick('');
  };
  const saveDesign = async () => {
    const nm = (saveAs || '').trim();
    if (!c.design || !nm) return;
    const { error } = await panelPost('email', 'design_save', {
      name: nm, description: saveAsDesignNote || `From the e-mail “${c.subject}”`, doc: forWire(c.design) });
    if (error) { setNote({ kind: 'error', text: error }); return; }
    setNote({ kind: 'ok', text: `Saved to Email designs as “${nm}”.` });
    setSaveAs(null);
    if (!designs) setDesignsTick(t => t + 1);
  };

  const chips = (
    <div className="flex flex-col gap-1 text-xs text-grayscale-1100">
      <span>Placeholders — click to insert where you are typing (filled per person)</span>
      <div className="flex flex-wrap gap-1">
        {placeholders.map(p => (
          <button key={p.name} type="button" title={p.meaning} disabled={disabled}
            onMouseDown={e => e.preventDefault() /* keep the caret where it is */}
            onClick={() => insert(p.name)}
            className="px-2 py-0.5 rounded-full border border-primary-300 bg-primary-100/50 text-[11px] text-grayscale-1300 font-mono hover:bg-primary-100 disabled:opacity-50">
            {`{${p.name}}`}
          </button>
        ))}
      </div>
    </div>
  );
  const subjectInput = (
    <Labeled label="Subject">
      <input {...track('subject')} value={c.subject} maxLength={250} disabled={disabled}
        onChange={e => onChange({ subject: e.target.value })}
        placeholder="e.g. {first_name}, 3 technologies picked for {organisation}" className={inputCls} />
    </Labeled>
  );

  return (
    <div className="flex flex-col gap-3 min-w-0">
      {formats.length > 1 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-grayscale-1100 mr-1">Format</span>
          {formats.map(f => (
            <button key={f} type="button" onClick={() => switchTo(f)} disabled={disabled}
              className={`py-1 px-3 rounded-full text-xs font-grotesk ${c.format === f
                ? 'bg-primary-500 text-grayscale-100 font-bold'
                : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200 disabled:opacity-50'}`}>
              {FORMAT_LABEL[f]}
            </button>
          ))}
        </div>
      )}
      {hint}
      {note && (!note.fmt || note.fmt === c.format) && (note.kind === 'warn'
        ? <div className="text-xs rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">{note.text}</div>
        : <Msg kind={note.kind}>{note.text}</Msg>)}

      <fieldset disabled={disabled} className={`flex flex-col gap-3 min-w-0 ${disabled ? 'opacity-70 pointer-events-none' : ''}`}>
        {c.format === 'design' ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-end gap-2">
              <Labeled label="Start from a design (Email designs)">
                <select value={pick} onChange={e => setPick(e.target.value)} className={`${inputCls} !w-72`}>
                  <option value="">Choose a design…</option>
                  {dmeta?.builtins?.length ? <optgroup label="Built-in">{dmeta.builtins.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</optgroup> : null}
                  {dmeta?.saved?.length ? <optgroup label="Saved">{dmeta.saved.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</optgroup> : null}
                </select>
              </Labeled>
              <Btn small onClick={loadDesign} disabled={!pick}>Load</Btn>
              {c.design_from && <span className="text-xs text-grayscale-1100 pb-1.5">Started from “{c.design_from}”</span>}
              {allowSaveAsDesign && (
                <span className="ml-auto flex items-center gap-1">
                  {saveAs === null ? (
                    <Btn small onClick={() => setSaveAs(c.design_from ? `${c.design_from} (copy)` : (c.subject || 'New design').slice(0, 120))}
                      disabled={!c.design}>Save as a design…</Btn>
                  ) : (
                    <>
                      <input autoFocus value={saveAs} maxLength={120} onChange={e => setSaveAs(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveDesign(); if (e.key === 'Escape') setSaveAs(null); }}
                        className={`${inputCls} !w-52`} />
                      <Btn small kind="primary" onClick={saveDesign}>Save</Btn>
                      <Btn small onClick={() => setSaveAs(null)}>Cancel</Btn>
                    </>
                  )}
                </span>
              )}
            </div>
            {designsErr && <Msg kind="error">Email designs: {designsErr}</Msg>}
            {c.design && dmeta ? (
              <DesignBuilder doc={c.design} onChange={d => onChange({ design: d })}
                subject={c.subject} onSubject={v => onChange({ subject: v })}
                placeholders={placeholders} fonts={dmeta.fonts} palette={dmeta.palette}
                maxBlocks={dmeta.limits.blocks} maxButtons={dmeta.limits.buttons} />
            ) : <div className="text-xs text-grayscale-1100">Loading the designs…</div>}
          </div>
        ) : c.format === 'html' ? (
          <div className="flex flex-col gap-2">
            {subjectInput}
            {chips}
            <Labeled label="Body — use the toolbar for bold, italic, links and lists (or edit the HTML source)">
              <RichText ref={bodyRef} value={c.body} onChange={h => onChange({ body: h })} allowSource
                initialSource={!!c.raw_html}
                onFocus={() => { focused.current = 'body'; }} minHeight={260}
                placeholder="Hi {first_name}, …" />
            </Labeled>
            {c.raw_html && (
              <div className="text-[11px] text-amber-800">
                This is a whole HTML document, so it opens in the HTML source. The editor view keeps only simple
                formatting (bold, italic, links, lists): stay in the source to keep its layout.
              </div>
            )}
            <details open={!!c.text}>
              <summary className="text-xs text-grayscale-1100 cursor-pointer">
                Plain-text version (optional — made from the HTML when left empty)
              </summary>
              <textarea {...track('text')} value={c.text} rows={6}
                onChange={e => onChange({ text: e.target.value })}
                placeholder="Leave empty to use the text of the HTML body"
                className="mt-1 w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 font-mono" />
            </details>
            <div className="text-[11px] text-grayscale-1100">
              {'{Name}'} and {'{University Name}'} from the older campaign tool still work (first name, organisation).
              Links to universitytech.io get campaign tracking added per person when sent.
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {subjectInput}
            <Labeled label="Body — plain text; a blank line starts a paragraph, lines starting “• ” or “- ” make a list">
              <textarea {...track('body')} value={c.body} rows={16}
                onChange={e => onChange({ body: e.target.value })}
                placeholder={'Hi {first_name},\n\n…'}
                className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-xs font-mono text-grayscale-1300 leading-relaxed" />
            </Labeled>
            {chips}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-grayscale-1100">
                  Buttons — up to {MAX_CTAS}; shown above the sign-off, and as “Label: link” lines in the plain-text part.
                  For a literal brace type {'{{'} or {'}}'}.
                </span>
                <Btn small disabled={disabled || c.ctas.length >= MAX_CTAS}
                  onClick={() => onChange({ ctas: [...c.ctas, { label: '', url: '' }] })}>
                  + Add button
                </Btn>
              </div>
              {c.ctas.map((x, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input {...track(`cta_label_${i}`)} value={x.label} placeholder="Label, e.g. Upgrade to {plan_name}"
                    onChange={e => setField(`cta_label_${i}`, e.target.value)}
                    className={`${inputCls} !w-2/5`} />
                  <input {...track(`cta_url_${i}`)} value={x.url} placeholder="https://… · mailto:… · {upgrade_url}"
                    onChange={e => setField(`cta_url_${i}`, e.target.value)}
                    className={`${inputCls} font-mono !text-xs`} />
                  <button type="button" title="Remove this button"
                    onClick={() => onChange({ ctas: c.ctas.filter((_, j) => j !== i) })}
                    className="w-7 h-7 shrink-0 rounded-full hover:bg-grayscale-200 text-grayscale-1100">×</button>
                </div>
              ))}
              {!c.ctas.length && <div className="text-[11px] text-grayscale-1100">No buttons.</div>}
            </div>
          </div>
        )}
      </fieldset>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Fallbacks: what a placeholder becomes for someone with no value
// ---------------------------------------------------------------------------

/** Placeholders that are always filled (or blank on purpose): nothing to fall back to
 *  (admin_email_targeting._ALWAYS_FILLED / _EMPTY_ON_PURPOSE). */
const NO_FALLBACK = new Set(['email', 'today', 'searches', 'saved_count', 'profile_pct', 'trial_days', 'site_url',
  'upgrade_url', 'tech_count', 'university_count', 'country_count', 'profile_raw_pct', 'cards_opened',
  'unsubscribe_url', 'winback_paragraph']);
/** What a placeholder becomes when nothing says otherwise, before the saved global list loads
 *  (email_templates.CODE_FALLBACKS; the live values come from useDefaultFallbacks). */
export const DEFAULT_FALLBACKS: Record<string, string> = { first_name: 'member', name: 'member', plan_name: 'Voyager' };

// The global fallbacks (Email Center → Default fallbacks): read once per page load and shared;
// a save there announces itself so every open panel shows the new values.
const FALLBACKS_EVENT = 'utech:default-fallbacks';
let globalFallbacks: Record<string, string> | null = null;
let globalFallbacksLoad: Promise<void> | null = null;
export const announceDefaultFallbacks = (v: Record<string, string>) => {
  globalFallbacks = { ...DEFAULT_FALLBACKS, ...v };
  window.dispatchEvent(new Event(FALLBACKS_EVENT));
};
export const useDefaultFallbacks = (): Record<string, string> => {
  const [v, setV] = useState<Record<string, string>>(globalFallbacks || DEFAULT_FALLBACKS);
  useEffect(() => {
    const sync = () => setV(globalFallbacks || DEFAULT_FALLBACKS);
    window.addEventListener(FALLBACKS_EVENT, sync);
    if (!globalFallbacks) {
      globalFallbacksLoad = globalFallbacksLoad || panelGet('email', 'default_fallbacks').then(({ data }) => {
        const fb = (data as any)?.fallbacks;
        if (fb && typeof fb === 'object') announceDefaultFallbacks(fb);
        else globalFallbacksLoad = null;      // try again on the next panel
      });
    } else sync();
    return () => window.removeEventListener(FALLBACKS_EVENT, sync);
  }, []);
  return v;
};

/** The placeholders in an e-mail someone may have no value for (so a fallback applies). */
/** Of these placeholder names, the ones someone may have no value for (a fallback applies). */
export const fallbackNames = (names: string[]): string[] =>
  names.filter(p => !NO_FALLBACK.has(p) && !p.startsWith('cta_url_'));

export const fallbackable = (c: EditableContent): string[] =>
  placeholdersIn(c.subject, c.format === 'design' ? '' : c.body, c.format === 'html' ? c.text : '',
    c.format === 'design' && c.design ? JSON.stringify(c.design) : '',
    ...(c.format === 'text' ? c.ctas.flatMap(x => [x.label, x.url]) : []))
    .filter(p => !NO_FALLBACK.has(p) && !p.startsWith('cta_url_'));

/**
 * What each placeholder becomes for someone with no value (placeholder_fallbacks:
 * a trigger's, a saved template's). Blank = what it inherits: the defaults, or
 * — over a saved template — the template's own. Campaigns has its own, fuller
 * panel (campaigns/PlaceholderFallbacks: who is missing each value).
 */
export const FallbacksPanel: React.FC<{
  /** The placeholders to offer: fallbackable(content), or the ones a template uses. */
  names: string[];
  value: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
  /** What each becomes when left blank here (default: the global fallbacks). */
  inherited?: Record<string, string>;
  title?: string;
  /** The summary when none is set here. */
  unsetLabel?: string;
  disabled?: boolean;
}> = ({ names, value, onChange, inherited: given, title = 'When someone has no value',
        unsetLabel = 'the defaults', disabled }) => {
  const global = useDefaultFallbacks();
  const inherited = given || global;
  if (!names.length) return null;
  const set = names.filter(p => value[p] !== undefined).length;
  const put = (p: string, v: string) => {
    const next = { ...value };
    if (v) next[p] = v; else delete next[p];
    onChange(next);
  };
  return (
    <details className="text-xs" open={set > 0}>
      <summary className="cursor-pointer text-grayscale-1100">
        {title} {set ? `(${set} set)` : `(${unsetLabel})`}
      </summary>
      <div className="mt-2 grid md:grid-cols-3 gap-2">
        {names.map(p => (
          <Labeled key={p} label={`{${p}}`}
            hint={`blank = ${inherited[p] ? `“${inherited[p]}”` : 'left empty'}`}>
            <input value={value[p] ?? ''} maxLength={200} disabled={disabled}
              onChange={e => put(p, e.target.value)} className={inputCls} />
          </Labeled>
        ))}
      </div>
    </details>
  );
};

export default ContentEditor;
